import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SelectionService } from '../../../models/review/selection-service';
import { ProcessingQueueService } from '../../../services/processing-queue.service';
import { CasesService } from '../../../services/cases.service';
import { LexbeCase } from '../../../models/case.model';
import { UntypedFormControl } from '@angular/forms';
import { ProcessingQueueRequest } from '../../../models/processing-queue-request.model';
import { UserConfigs } from '../../../models/user-configs.model';
import { UserConfigsService } from '../../../services/user-configs.service';
import { combineLatest, Subscription } from 'rxjs';

@Component({
    selector: 'lex-export-operations',
    templateUrl: './export-operations.component.html',
    styleUrls: ['./export-operations.component.scss'],
    standalone: false
})
export class ExportOperationsComponent implements OnInit, OnDestroy {


  @Input() expandToEmailFamily: boolean;

  destinationCaseId: string;
  exportDownloadPath = new UntypedFormControl({ value: '', disabled: true });
  exportLogPath = new UntypedFormControl({ value: '', disabled: true });
  exportPattern = `\\\\\\\\(([aA][uU][sS][tT][iI][nN])|([aA][mM][aA][zZ][oO][nN]))\\\\[a-zA-Z0-9_\\-\\\\\\s]*`;
  exportLogPattern = `\\\\\\\\(([aA][uU][sS][tT][iI][nN])|([aA][mM][aA][zZ][oO][nN]))\\\\[a-zA-Z0-9._\\-\\\\\\s]*`;
  amazonServerURI = '\\\\amazon\\';
  austinServerURI = '\\\\austin\\';
  cases: LexbeCase[] = new Array<LexbeCase>();
  userConfig: UserConfigs;
  private subscription = new Subscription();

  constructor(
    public selectionService: SelectionService,
    public queueService: ProcessingQueueService,
    public caseService: CasesService,
    private userConfigService: UserConfigsService) { }

  ngOnInit() {
    const userConfigObservable = this.userConfigService.getUserConfigs();
    const caseObservable = this.caseService.getAllCases();
    this.subscription.add(
      combineLatest([userConfigObservable, caseObservable])
        .subscribe(data => {
          // data is an array of last subscriptions (next) of each of the observables above in the same order
          this.userConfig = data[0];
          this.cases = data[1].value.filter(c => c.caseId !== this.userConfig.currentCase.caseId); // Filter out the current case.
        }));

    this.selectionService.selection.changed.subscribe(() => {
      if (this.selectionService.selection.selected.length) {
        this.exportDownloadPath.enable();
        this.exportLogPath.enable();
      } else {
        this.exportDownloadPath.disable();
        this.exportLogPath.disable();
      }
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  downloadCase() {
    if (!this.exportDownloadPath.valid) {
      this.selectionService.operationAlert = 'Error in folder path';
      return;
    }

    if (!this.selectionService.selection.selected.length) {
      this.selectionService.operationAlert = 'No docs selected';
      return;
    }

    let functionId;
    let downloadPath;
    const tempFolderPath: string = this.exportDownloadPath.value.trimEnd('\\');
    if (tempFolderPath.toLowerCase().includes(this.amazonServerURI)) {
      functionId = 'e2f29564-b9c7-41ef-beb7-f068dfb708ec';
      downloadPath = this.formatPathString(tempFolderPath, this.amazonServerURI);
    } else if (tempFolderPath.toLowerCase().includes(this.austinServerURI)) {
      functionId = 'c5edc44d-8012-e811-a988-121a0f91a96a';
      downloadPath = this.formatPathString(tempFolderPath, this.austinServerURI);
    } else {
      this.selectionService.operationAlert = 'Invalid path';
      return;
    }

    const argument = JSON.stringify({
      folderPath: `${downloadPath}`,
      filterId: this.selectionService.getSavedFilterId() ? `${this.selectionService.getSavedFilterId()}` : undefined,
      selectAll: `${this.selectionService.selectAllFlag}`,
      docIds: `${this.selectionService.selection.selected.map(s => s.docId)}`,
      expandToEmailFamily: this.expandToEmailFamily
    });

    const step = new ProcessingQueueRequest();
    step.functionId = functionId;
    step.functionArgument = argument;
    this.queueService.addIncomingFunction(step).subscribe(next => {
      if (next)
        this.selectionService.operationAlert = `Download started: ${downloadPath}`;
    });
  }

  downloadExcelLog() {
    if (!this.exportLogPath.valid) {
      this.selectionService.operationAlert = 'Error in file path';
      return;
    }

    if (!this.exportLogPath.value.includes('.xlsx')) {
      this.selectionService.operationAlert = 'File path must be in .xlsx format';
      return;
    }

    if (!this.selectionService.selection.selected.length) {
      this.selectionService.operationAlert = 'No docs selected';
      return;
    }

    let functionId;
    let downloadFilePath;
    const tempFilePath: string = this.exportLogPath.value.trimEnd();
    if (tempFilePath.toLowerCase().includes(this.amazonServerURI)) {
      functionId = '08ec21e9-65bc-4def-b221-7137e48deaee';
      downloadFilePath = this.formatPathString(tempFilePath, this.amazonServerURI);
    } else if (tempFilePath.toLowerCase().includes(this.austinServerURI)) {
      functionId = '864fbe87-64ac-4173-af3f-60a72e784179';
      downloadFilePath = this.formatPathString(tempFilePath, this.austinServerURI);
    } else {
      this.selectionService.operationAlert = 'Invalid path';
      return;
    }

    const argument = JSON.stringify({
      userId: `${this.userConfig.userId}`,
      filePath: `${downloadFilePath}`,
      filterId: this.selectionService.getSavedFilterId() ? `${this.selectionService.getSavedFilterId()}` : undefined,
      selectAll: `${this.selectionService.selectAllFlag}`,
      docIds: `${this.selectionService.selection.selected.map(s => s.docId)}`,
      expandToEmailFamily: this.expandToEmailFamily
    });

    const step = new ProcessingQueueRequest();
    step.functionId = functionId;
    step.functionArgument = argument;
    this.queueService.addIncomingFunction(step).subscribe(next => {
      if (next)
        this.selectionService.operationAlert = `Export started: ${downloadFilePath}`;
    });
  }

  formatPathString(path: string, uri: string): string {
    const tempFolderPath = path.toLowerCase().replace(uri, '');
    return [tempFolderPath.slice(0, 1), ':', tempFolderPath.slice(1, tempFolderPath.length)].join('');
  }

  copyCase() {
    if (!this.selectionService.selection.selected.length) {
      this.selectionService.operationAlert = 'No docs selected';
      return;
    }

    if (!this.destinationCaseId) {
      this.selectionService.operationAlert = 'No Destination Case selected';
      return;
    }

    const argument = JSON.stringify({
      'destinationCaseId': `${this.destinationCaseId}`,
      'filterId': this.selectionService.getSavedFilterId() ? `${this.selectionService.getSavedFilterId()}` : undefined,
      'selectAll': `${this.selectionService.selectAllFlag}`,
      'docIds': `${this.selectionService.selection.selected.map(s => s.docId)}`
    });
    const step = new ProcessingQueueRequest();
    step.functionId = 'fc5027de-daa2-4334-a1e8-f59386a55924';
    step.functionArgument = argument;
    this.queueService.addIncomingFunction(step).subscribe(next => {
      if (next)
        this.selectionService.operationAlert = `Case copy started`;
    });
  }

  setDestinationCase(id: string) {
    this.destinationCaseId = id;
  }
}
